import { useRouter } from "next/router";

import { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { format, isValid } from "date-fns";
import { isArray, orderBy } from "lodash";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import {
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faInfoCircle,
  faSpinner,
  faStickyNote,
} from "@fortawesome/free-solid-svg-icons";

import CustomCard from "components/CustomCard";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import Table from "components/Table";
import { useApi } from "hooks/useApi";
import { useGlobalState } from "hooks/useGlobalState";
import { baseFetcher } from "utils/baseFetcher";

import PeersTradeComparisonLoading from "../Loading";
import { usePeersComparisonData } from "../PeersTradeComparison";

export const mnaTransactionsComparableUrl = (entityId: any) => {
  return `/api/v3/merging-acquisitions/${entityId}/transactions-comparison`;
};

export const basicColumns = [
  {
    title: "Target",
    dataIndex: "target_name",
    key: "target_name",
    style: {
      minWidth: 200,
    },
    render: (value: string, record: any) => {
      return `${value} (${record.target_country})`;
    },
  },
  {
    title: "Deal Value (USDm)",
    dataIndex: "deal_value_million_usd",
    className: "text-right",
    headerClassName: "text-right",
    key: "deal_value_million_usd",
    render: (value: number) => (value ? numeral(value).format("0,0") : "-"),
    style: {
      minWidth: 50,
    },
    renderHeader: (value: any) => {
      return (
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip>
              <p className="mb-0">
                Deal Value, also referred to as Equity Value
              </p>
            </Tooltip>
          }
        >
          <span className="cursor-pointer">
            {value}
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="text-[12px] ml-[4px]"
            />
          </span>
        </OverlayTrigger>
      );
    },
  },
  {
    title: "EV (USDm)",
    dataIndex: "transaction_value_ev_million_usd",
    key: "transaction_value_ev_million_usd",
    render: (value: number) => (value ? numeral(value).format("0,0") : "-"),
    style: {
      minWidth: 50,
    },
    renderHeader: (value: any) => {
      return (
        <OverlayTrigger
          placement="right"
          overlay={
            <Tooltip>
              <p className="mb-0">
                Implied EV here includes assumption of debt
              </p>
            </Tooltip>
          }
        >
          <span className="cursor-pointer">
            {value}
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="text-[12px] ml-[4px]"
            />
          </span>
        </OverlayTrigger>
      );
    },
    className: "text-right",
    headerClassName: "text-right",
  },
  {
    title: "EV/Sales",
    dataIndex: "transaction_ev_to_ltm_revenue",
    headerClassName: "text-right",
    className: "text-right",
    key: "transaction_ev_to_ltm_revenue",
    render: (value: number) =>
      value && value > 0 ? numeral(value).format("0,0.[00]") : "-",
  },
  {
    title: "EV/EBITDA",
    dataIndex: "transaction_ev_to_ltm_ebitda",
    className: "text-right",
    key: "transaction_ev_to_ltm_ebitda",
    render: (value: number) =>
      value && value > 0 ? numeral(value).format("0,0.[00]") : "-",
  },
  {
    title: "PE",
    headerClassName: "text-right",
    className: "text-right",
    dataIndex: "transaction_pe_ratio",
    key: "transaction_pe_ratio",
    render: (value: number) =>
      value && value > 0 ? numeral(value).format("0,0.[00]") : "-",
  },
];

export const additionalColumns = [
  {
    title: "Acquirer",
    dataIndex: "acquirer",
    key: "acquirer",
    render: (value: number, record: any) =>
      `${value} (${record.acquirer_country})`,
    style: {
      minWidth: 250,
    },
  },
  {
    title: "Announcement Date",
    dataIndex: "announcement_date",
    key: "announcement_date",
    render: (value: number) =>
      format(
        value
          ? isValid(new Date(value))
            ? new Date(value)
            : new Date()
          : new Date(),
        "dd MMM yyyy"
      ),
  },
  {
    title: "Stake Acquired",
    dataIndex: "stake_acquired",
    headerClassName: "text-right",
    className: "text-right",
    key: "stake_acquired",
    render: (value: number) => `${value}%`,
  },
  {
    title: "Deal Structure",
    dataIndex: "deal_structure",
    key: "deal_structure",
  },
  {
    title: "Investor Type",
    dataIndex: "investor_type",
    key: "investor_type",
  },
  {
    title: "Type Of Stake",
    dataIndex: "type_of_stake",
    key: "type_of_stake",
  },
  {
    title: "Financing Source",
    dataIndex: "financing_source",
    key: "financing_source",
  },
  {
    title: "Transaction Status",
    dataIndex: "transaction_status",
    key: "transaction_status",
  },
  {
    title: "Share Price 30 Days Prior",
    dataIndex: "share_price_30day_before",
    key: "share_price_30day_before",
    style: {
      minWidth: 100,
    },
    render: (value: number) => (value ? value : "-"),
  },
  {
    title: "Offer Price Per Share",
    dataIndex: "offer_price",
    key: "offer_price",
    style: {
      minWidth: 100,
    },
    render: (value: number) => (value ? value : "-"),
  },
  {
    title: "Share Price 30 Days After",
    dataIndex: "share_price_30day_after",
    key: "share_price_30day_after",
    style: {
      minWidth: 100,
    },
    render: (value: number) => (value ? value : "-"),
  },
];

export const conditionalColumns = [
  {
    title: "Description",
    dataIndex: "transaction_description",
    key: "transaction_description",
    style: {
      minWidth: 100,
    },
    render: (value: any) => {
      return (
        <div className="flex flex-1 items-center justify-center">
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip>
                <p>{value}</p>
              </Tooltip>
            }
          >
            <p className="mb-0 ml-[8px] cursor-pointer">
              <FontAwesomeIcon
                icon={faStickyNote}
                className="text-[20px] text-yellow-400"
              />
            </p>
          </OverlayTrigger>
        </div>
      );
    },
  },
];

export const exportColumns = [
  ...basicColumns,
  ...additionalColumns,
  ...conditionalColumns,
];

export const useTransactionComparisonData = (
  entityId: string,
  embed?: boolean
) => {
  const [mnaTransactionsLoading, setMnaTransactionsLoading] = useGlobalState(
    `mnaTransactionsLoading-${entityId}`,
    true
  );
  const [_isExpanded, setIsExpanded] = useGlobalState(
    `mnaTransactionsIsExpanded`
  );

  const { data: comparisons, isLoading } = useApi(
    entityId ? mnaTransactionsComparableUrl(entityId) : null,
    baseFetcher,
    {
      revalidateFirstPage: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      revalidateOnMount: !embed,
      onSuccess: (data: any) => {
        if (data?.status !== "enqueued") {
          setIsExpanded(true);
          setMnaTransactionsLoading(false);
        }
      },
      onError: () => {
        setIsExpanded(true);
        setMnaTransactionsLoading(false);
      },
    }
  );

  return {
    comparisons,
    isLoading: isLoading || (!embed && mnaTransactionsLoading),
  };
};

export default function PeersTransactionsComparison({
  embed,
}: {
  embed?: boolean;
}) {
  const router = useRouter();
  const entityId = router?.query["entity-id"] as string;

  const [showScroll, setShowScroll] = useState(true);
  const [isExpanded, setIsExpanded] = useGlobalState(
    `mnaTransactionsIsExpanded`,
    false
  );

  const { comparisons, isLoading } = useTransactionComparisonData(
    entityId,
    embed
  );

  const { isLoading: peersLoading } = usePeersComparisonData(entityId, embed);

  const columns = useMemo(() => {
    const columns = [...basicColumns, ...additionalColumns];

    if (!embed) {
      columns.splice(1, 0, ...(conditionalColumns as any));
    }

    return columns;
  }, [embed]);

  const content = useMemo(() => {
    if (isLoading) {
      return <PeersTradeComparisonLoading type="transactions" />;
    }

    if (!isArray(comparisons)) return null;

    return (
      <div className="relative">
        <div
          className="max-w-full overflow-x-auto"
          id="transaction-table"
          onScroll={(e) => {
            const target = e.nativeEvent.target as any;
            const scrollLeft = target.scrollLeft;
            const scrollWidth = target.scrollWidth;
            const scrollPos = scrollWidth - scrollLeft - 100;
            const offset = target.offsetWidth;

            if (scrollPos <= offset && showScroll) {
              setShowScroll(false);
            } else if (scrollPos > offset && !showScroll) {
              setShowScroll(true);
            }
          }}
        >
          <Table
            columns={columns}
            dataSource={orderBy(comparisons, "announcement_date", "desc")}
            tableClassName="!mb-0 !border-0"
            headRowClassName="!bg-white"
            headCellClassName="!border-r-0 !border-y-1 !border-solid !border-y-slate-200 !py-[10px]"
            rowClassName="!bg-white"
            cellClassName="!border-r-0 !border-y-1 !border-solid !border-y-slate-200 !py-[10px]"
            rowClassNameCallback={(record: any) => {
              if (!record) return "";

              return "!bg-white";
            }}
          />
        </div>
        {!embed && !showScroll && (
          <div
            className={twMerge(
              "absolute top-0 left-0 bottom-0 w-[100px] bg-gradient-to-r from-smart-teal/30 to-transparent",
              "flex items-center justify-center group cursor-pointer"
            )}
            onClick={() => {
              document.getElementById("transaction-table")?.scrollTo({
                left: 0,
                behavior: "smooth",
              });
            }}
          >
            <FontAwesomeIcon
              icon={faChevronLeft}
              className="text-[20px] group-hover:translate-x-[5px] transition-all"
            />
          </div>
        )}
        {!embed && showScroll && (
          <div
            className={twMerge(
              "absolute top-0 right-0 bottom-0 w-[100px] bg-gradient-to-l from-smart-teal/30 to-transparent",
              "flex items-center justify-center group cursor-pointer"
            )}
            onClick={() => {
              const width =
                document.getElementById("transaction-table")?.clientWidth;
              document.getElementById("transaction-table")?.scrollTo({
                left: width,
                behavior: "smooth",
              });
            }}
          >
            <FontAwesomeIcon
              icon={faChevronRight}
              className="text-[20px] group-hover:translate-x-[5px] transition-all"
            />
          </div>
        )}
      </div>
    );
  }, [columns, comparisons, embed, isLoading, showScroll]);

  useEffect(() => {
    if (!isLoading || !peersLoading) {
      setIsExpanded(true);
    }
  }, [isLoading, peersLoading, setIsExpanded]);

  if (!entityId) return null;

  return (
    <CustomCard className="overflow-x-hidden p-0 shadow-none border border-solid border-slate-200">
      <div className="flex items-center justify-between p-[15px] bg-intelligent-silver/10">
        <div className="flex items-center gap-[15px]">
          <div
            className="flex items-center justify-center w-[40px] h-[40px] rounded-full cursor-pointer hover:bg-intelligent-silver/20"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <FontAwesomeIcon
              icon={isExpanded ? faChevronDown : faChevronRight}
            />
          </div>
          <div className="flex flex-col">
            <span className="flex flex-col md:flex-row md:items-center gap-[5px]">
              <h3 className="text-[20px] font-bold mb-0">
                Precedent Transaction Comparables
              </h3>
              {comparisons?.[0]?.created_at && (
                <span className="text-[12px]">
                  {" "}
                  As of{" "}
                  {format(
                    new Date(comparisons?.[0]?.created_at),
                    "dd MMM yyyy"
                  )}
                </span>
              )}
            </span>
            <span className="text-[12px]">
              Implied multiples based on reported transactions on a LTM basis
            </span>
          </div>
        </div>
        {isLoading && !isExpanded && (
          <div className="flex items-center gap-[15px] mr-[10px]">
            <FontAwesomeIcon
              icon={faSpinner}
              className="animate-spin text-[20px] text-smart-teal"
            />
          </div>
        )}
      </div>
      <div className={twMerge(!isExpanded && "hidden")}>{content}</div>
    </CustomCard>
  );
}
